/** Utilidades de presentación compartidas entre drawers del chat. */

export const chatInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')

export const formatChatLastMessageAt = (value: string | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
  }).format(date)
}

/** Fecha y hora en zona local del navegador (ISO del backend). */
export const formatChatMessageDateTime = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date)
}

export type TChatTextSegment =
  | { type: 'text'; text: string }
  | { type: 'link'; href: string; label: string }

const trimTrailingPunctFromUrl = (raw: string): string =>
  raw.replace(/[.,;:!?]+$/, '')

const isSafeHttpUrl = (href: string): boolean => {
  try {
    const u = new URL(href)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Divide el cuerpo del mensaje en fragmentos de texto y enlaces http(s).
 * Solo se reconocen URLs con esquema explícito (https?://).
 */
export const parseMessageBodyWithLinks = (body: string): TChatTextSegment[] => {
  if (body === '') return [{ type: 'text', text: '' }]

  const re = /(https?:\/\/[^\s]+)/gi
  const segments: TChatTextSegment[] = []
  let last = 0

  for (const match of body.matchAll(re)) {
    const raw = match[0]
    const index = match.index ?? 0
    if (index > last) {
      segments.push({ type: 'text', text: body.slice(last, index) })
    }
    const href = trimTrailingPunctFromUrl(raw)
    if (isSafeHttpUrl(href)) {
      segments.push({
        type: 'link',
        href,
        label: href.length < raw.length ? href : raw,
      })
    } else {
      segments.push({ type: 'text', text: raw })
    }
    last = index + raw.length
  }

  if (last < body.length) {
    segments.push({ type: 'text', text: body.slice(last) })
  }

  if (segments.length === 0) {
    return [{ type: 'text', text: body }]
  }

  return segments
}
